<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useDataSourcesStore } from '@product/stores/dataSources'

const dataSourcesStore = useDataSourcesStore()

// Keyboard shortcuts documentation
const keybinds = [
  {
    keys: ['Ctrl', 'Space'],
    description: 'Show all available fields or filter by current partial word',
    context: 'While typing in query input'
  },
  {
    keys: ['Tab'],
    description: 'Cycle forward through dropdown suggestions or complete single match',
    context: 'When autocomplete is active'
  },
  {
    keys: ['Shift', 'Tab'],
    description: 'Cycle backward through dropdown suggestions',
    context: 'When autocomplete dropdown is visible'
  },
  {
    keys: ['Enter'],
    description: 'Accept the currently selected autocomplete suggestion',
    context: 'When autocomplete dropdown is visible'
  },
  {
    keys: ['↓', '↑'],
    description: 'Navigate up and down through autocomplete suggestions',
    context: 'When autocomplete dropdown is visible'
  },
  {
    keys: ['Escape'],
    description: 'Close the autocomplete dropdown',
    context: 'When autocomplete is active'
  },
  {
    keys: ['Ctrl', 'Enter'],
    description: 'Submit the query and execute search',
    context: 'While typing in query input'
  }
]

// Derive categories from the store's fieldsByLogType getter
const fieldCategories = computed(() => {
  const byLogType = dataSourcesStore.fieldsByLogType
  return Object.entries(byLogType)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([logType, fields]) => ({
      name: logType,
      fields
    }))
})

// Scroll spy state
const activeSection = ref('keyboard-shortcuts')
const sectionRefs = ref([])
const scrollSpyNav = ref(null)
const contentContainer = ref(null)
let observer = null

// Navigation items for scroll spy
const navItems = computed(() => {
  const items = [
    { id: 'keyboard-shortcuts', label: 'Keyboard Shortcuts' }
  ]

  fieldCategories.value.forEach((category, index) => {
    items.push({
      id: `category-${index}`,
      label: category.name
    })
  })

  return items
})

// Setup Intersection Observer for scroll tracking
function setupIntersectionObserver() {
  if (!contentContainer.value) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activeSection.value = entry.target.id
        }
      })
    },
    {
      root: contentContainer.value,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    }
  )

  sectionRefs.value.forEach((section) => {
    if (section) observer.observe(section)
  })
}

// Smooth scroll to section
function scrollToSection(sectionId) {
  const targetSection = sectionRefs.value.find(el => el && el.id === sectionId)
  if (targetSection && contentContainer.value) {
    const scrollSpyHeight = scrollSpyNav.value?.offsetHeight || 0
    const targetPosition = targetSection.offsetTop - scrollSpyHeight - 16 // 16px extra padding

    contentContainer.value.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    })
  }
}

// Lifecycle hooks
onMounted(() => {
  setupIntersectionObserver()
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<template>
  <div class="guide-container">
    <!-- Fixed Header -->
    <div class="guide-header">
      <h4>Query guide</h4>
      <p class="subtitle">Field reference for Lucene query syntax</p>
    </div>

    <!-- Scrollable Content -->
    <div ref="contentContainer" class="guide-content">
      <!-- Sticky Scroll Spy Navigation -->
      <div ref="scrollSpyNav" class="scroll-spy-nav">
        <button
          v-for="item in navItems"
          :key="item.id"
          class="spy-item"
          :class="{ active: activeSection === item.id }"
          @click="scrollToSection(item.id)"
        >
          {{ item.label }}
        </button>
      </div>

      <!-- Keyboard Shortcuts Section -->
      <section
        :ref="el => sectionRefs[0] = el"
        id="keyboard-shortcuts"
        class="mb-5"
      >
        <h5 class="category-title">Keyboard Shortcuts</h5>
        <p class="category-description">Quick reference for query input shortcuts</p>
        <div class="keybind-list">
          <div v-for="(keybind, index) in keybinds" :key="index" class="keybind-item box dark padded mb-3">
            <div class="keybind-header">
              <div class="key-combination">
                <kbd v-for="(key, keyIndex) in keybind.keys" :key="keyIndex" class="key-badge">
                  {{ key }}
                </kbd>
              </div>
              <span class="keybind-context">{{ keybind.context }}</span>
            </div>
            <p class="keybind-description">{{ keybind.description }}</p>
          </div>
        </div>
      </section>

      <!-- Empty state -->
      <section v-if="fieldCategories.length === 0" class="mb-5">
        <p class="category-description">No fields available. Fields will appear here once your sources have data.</p>
      </section>

      <!-- Field Categories (by log type) -->
      <section
        v-for="(category, index) in fieldCategories"
        :key="category.name"
        :ref="el => sectionRefs[index + 1] = el"
        :id="`category-${index}`"
        class="mb-5"
      >
        <h5 class="category-title">{{ category.name }}</h5>
        <div class="field-list">
          <div v-for="field in category.fields" :key="field.name" class="field-item box dark padded mb-3">
            <div class="field-header">
              <code class="field-name">{{ field.name }}</code>
              <span class="field-type">{{ field.type }}</span>
            </div>
            <p v-if="field.description" class="field-description">{{ field.description }}</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.guide-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.guide-header {
  flex-shrink: 0;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--surface-raised);
}

.guide-header h4 {
  margin-bottom: 0.5rem;
}

.guide-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  position: relative;
}

/* Scroll Spy Navigation */
.scroll-spy-nav {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background-color: var(--surface-sunken);
  border-bottom: 1px solid var(--surface-raised);
  overflow-x: auto;
  overflow-y: hidden;
}

.scroll-spy-nav::-webkit-scrollbar {
  height: 4px;
}

.scroll-spy-nav::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-spy-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-xs);
}

.scroll-spy-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.15);
}

.spy-item {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  background-color: transparent;
  color: var(--text-muted);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.spy-item:hover {
  background-color: rgba(255, 255, 255, 0.03);
  color: var(--text-primary);
  border-color: rgba(255, 255, 255, 0.12);
}

.spy-item.active {
  background-color: rgba(168, 85, 247, 0.15);
  color: var(--accent-text);
  border-color: rgba(168, 85, 247, 0.4);
}

/* Content sections */
section {
  padding: 2rem 2rem 0 2rem;
}

.subtitle {
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-bottom: 0;
}

.category-title {
  color: white;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.category-description {
  color: var(--text-muted);
  font-size: 0.95rem;
  margin-bottom: 1rem;
  line-height: 1.5;
  padding-bottom: 0.5rem;
}

/* Keybind Section */
.keybind-item {
  background-color: var(--surface-inset) !important;
  border: 1px solid var(--surface-raised);
}

.keybind-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.key-combination {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.key-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: rgba(168, 85, 247, 0.12);
  color: var(--accent-text);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.keybind-context {
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-style: italic;
}

.keybind-description {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-bottom: 0;
  line-height: 1.4;
}

/* Field Items */
.field-item {
  background-color: var(--surface-inset) !important;
  border: 1px solid var(--surface-raised);
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.field-name {
  color: var(--accent-text);
  font-size: 0.9rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: rgba(168, 85, 247, 0.08);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(168, 85, 247, 0.3);
}

.field-type {
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-style: italic;
}

.field-description {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-bottom: 0;
}
</style>
